import clsx from 'clsx';
import type React from 'react';
import type { ComponentProps } from 'react';
import type { PrefetchMode } from '@//hooks/useLinkPrefetch';
import { AppLink } from '../AppLink';
import { Count } from '../Count';
import styles from './FilterButton.module.scss';

type NextLinkPropsSubset = Pick<ComponentProps<typeof AppLink>, 'replace' | 'scroll' | 'locale'>;

interface SharedFilterButtonProps {
  children: React.ReactNode;
  title?: string;
  isActive?: boolean;
  count?: string;
  countAriaLabel?: string;
  testId?: string;
}

type FilterButtonProps = SharedFilterButtonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined; // Disallow href for button
  };

type FilterLinkProps = SharedFilterButtonProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
  NextLinkPropsSubset & {
    href: string;
    /** Prefetch policy. Default 'none' — explicit opt-in required. */
    prefetch?: PrefetchMode;
  };

export type FilterButtonComponentProps = FilterButtonProps | FilterLinkProps;

export const FilterButton = (props: FilterButtonComponentProps) => {
  const { children, title, isActive, count, countAriaLabel, testId, ...rest } = props;

  const content = (
    <>
      {children}
      {count && (
        <Count aria-label={countAriaLabel} className={styles.count}>
          {count}
        </Count>
      )}
    </>
  );

  if ('href' in props && props.href) {
    const {
      href,
      replace,
      scroll,
      locale,
      prefetch,
      title: _,
      ...anchorProps
    } = rest as FilterLinkProps;
    return (
      <AppLink
        href={href}
        replace={replace}
        scroll={scroll}
        locale={locale}
        prefetch={prefetch}
        className={clsx(styles.button, {
          [styles.activeBackground]: isActive,
        })}
        title={title}
        data-testid={testId}
        {...anchorProps}
      >
        {content}
      </AppLink>
    );
  }

  const { title: _, ...buttonProps } = rest as FilterButtonProps;
  return (
    <button
      type={buttonProps.type || 'button'}
      className={clsx(styles.button, {
        [styles.activeBackground]: isActive,
      })}
      title={title}
      data-testid={testId}
      {...buttonProps}
    >
      {content}
    </button>
  );
};
